/* eslint-disable @typescript-eslint/ban-types */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  IconButton,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';
import TimerIcon from '@material-ui/icons/Timer';
import React, { useEffect, useMemo, useState } from 'react';
import { MdExpandMore } from 'react-icons/md';
import { unitSchedule } from '../../../../services/servico';
import { SearchI, ServicoUnidadeI } from '../../../SearchService';
import { useLocalizationStyles } from './styles';

interface Props {
  service: SearchI;
}

type BusinessHoursData = {
  dia_semana: string[];
  intervalo_ativo: boolean;
  hora_inicio_manha: string;
  hora_inicio_tarde: string;
  hora_fim_manha: string;
  hora_fim_tarde: string;
};

export default function Localization({ service }: Props): JSX.Element {
  const { classes, colors } = useLocalizationStyles();
  const [expanded, setExpanded] = useState<string | false>('panel1');
  const [BusinessHours, setBusinessHours] = useState<BusinessHoursData>();
  const [isOpen, setIsOpen] = useState(false);
  const [unidadeSelected, setUnidadeSelected] = useState<ServicoUnidadeI>(
    service.servicosUnidade[0],
  );
  const handleChangeUnity = (unityId: string) => {
    const unidadeClick = service.servicosUnidade.filter(
      // eslint-disable-next-line radix
      unidade => unidade.unidade.slug_unidade === unityId,
    );
    setUnidadeSelected(unidadeClick[0]);
  };

  function isBusinessOpen(BusinessHours) {
    const currentDay = new Date().getDay();
    const currentTime = new Date().toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });

    // Se for sábado (6) ou domingo (0), a empresa está fechada
    if (currentDay === 6 || currentDay === 0) {
      return false;
    }

    // Função auxiliar para verificar se o horário atual está dentro do intervalo
    function isTimeWithinInterval(time, start, end) {
      if (start === 'None' || end === 'None') {
        return false;
      }
      return time >= start && time <= end;
    }

    // Verificar se o momento atual está dentro dos intervalos de tempo
    if (
      isTimeWithinInterval(
        currentTime,
        BusinessHours.hora_inicio_manha,
        BusinessHours.hora_fim_manha,
      ) ||
      isTimeWithinInterval(
        currentTime,
        BusinessHours.hora_inicio_tarde,
        BusinessHours.hora_fim_tarde,
      ) ||
      (BusinessHours.hora_inicio_manha !== 'None' &&
        BusinessHours.hora_fim_tarde !== 'None' &&
        currentTime >= BusinessHours.hora_inicio_manha &&
        currentTime <= BusinessHours.hora_fim_tarde)
    ) {
      return true;
    }

    return false;
  }

  const handleAvaliar = async (slug_unidade: string) => {
    try {
      if (slug_unidade) {
        const { data } = await unitSchedule({ slug_unidade: slug_unidade });
        if (data) {
          setBusinessHours(data[0] ? data[0].horario_unidade : []);
        } else {
          setBusinessHours(data[0]?.horario_unidade || []);
        }
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (unidadeSelected) {
      handleAvaliar(unidadeSelected.unidade.slug_unidade);
    }
  }, [service, unidadeSelected]);

  function formatTime(timeStr) {
    if (timeStr === 'None') return null;
    const [hour, minute] = timeStr.split(':');
    return `${hour}:${minute}`;
  }

  const handleChangeAccordion =
    (panel: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      setExpanded(isExpanded ? panel : false);
    };

  const sortedUnidades = useMemo(() => {
    return service.servicosUnidade?.sort((a, b) =>
      a.unidade.nome.toLowerCase().normalize('NFD').localeCompare(
        b.unidade.nome.toLowerCase().normalize('NFD')
      ),
    );
  }, [service.servicosUnidade]);

  return (
    <Box className={classes.main}>
      <Box className={classes.content}>
        <Typography className={classes.textTitleSm}>Onde solicitar</Typography>
        <Box className={classes.boxUnitysList}>
          <Accordion
            expanded={expanded === 'panel1'}
            onChange={handleChangeAccordion('panel1')}
            classes={{
              root: classes.accordion,
            }}
          >
            <AccordionSummary
              expandIcon={<MdExpandMore className={classes.colorIcon} />}
              classes={{
                root: classes.boxTitleUnits,
                content: classes.boxTitleExpanded,
              }}
            >
              <Typography className={classes.titleUnits}>
                Unidades disponíveis
              </Typography>
            </AccordionSummary>
            <AccordionDetails classes={{ root: classes.accordionDetails }}>
              {sortedUnidades?.map(unidade => (
                <Typography
                  key={unidade.id}
                  className={
                    unidade.id === unidadeSelected.id
                      ? `${classes.unityName} ${classes.selectedUnityName}`
                      : classes.unityName
                  }
                  onClick={() =>
                    handleChangeUnity(unidade.unidade.slug_unidade)
                  }
                >
                  {unidade.unidade.nome}
                </Typography>
              ))}
            </AccordionDetails>
          </Accordion>
        </Box>
        <Box className={classes.boxLocalizationSide}>
          <Typography className={classes.textTitle}>Onde solicitar</Typography>
          <Box className={classes.localizationContent}>
            <Box className={classes.cardLocalization}>
              <Box className={classes.boxLocalization}>
                {unidadeSelected.unidade.source.indexOf(
                  'https://www.google.com/maps/embed?',
                ) !== -1 ? (
                  <iframe
                    width='100%'
                    height='226px'
                    src={unidadeSelected.unidade.source}
                    title='endereço'
                  />
                ) : (
                  <Typography
                    style={{
                      color: colors.textBody,
                      marginLeft: 20,
                    }}
                  >
                    Não foi possível carregar o mapa.
                  </Typography>
                )}
              </Box>
              <Box className={classes.localizationInfo}>
                <Typography className={classes.localeTitle}>
                  {unidadeSelected.unidade.nome}
                </Typography>
                <Typography className={classes.localizationSubTitle}>
                  {unidadeSelected.unidade.endereco} -{' '}
                  {unidadeSelected.unidade.bairro} -{' '}
                  {unidadeSelected.unidade.complemento && (
                    <>{unidadeSelected.unidade.complemento}</>
                  )}
                </Typography>
                <Box style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                  {unidadeSelected.agendamento && (
                    <Typography className={classes.tag}>Agendável</Typography>
                  )}
                  {unidadeSelected.atendimento && (
                    <Typography className={classes.tag}>Digital</Typography>
                  )}
                </Box>
                <Box style={{ marginRight: '24px', marginTop: '5px' }}>
                  <Box style={{ backgroundColor: '#fff' }}>
                    <Box
                      display='flex'
                      justifyContent='space-between'
                      alignItems='center'
                      onClick={() => setIsOpen(!isOpen)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Typography>Horário de funcionamento</Typography>
                      {BusinessHours &&
                        (isBusinessOpen(BusinessHours) ? (
                          <Typography color='primary'>Aberto</Typography>
                        ) : (
                          <Typography color='error'>Fechado</Typography>
                        ))}
                      <IconButton>
                        <ExpandMoreIcon
                          className={classes.iconSize}
                          style={{
                            transform: isOpen
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                            transition: 'transform 0.3s',
                          }}
                        />
                      </IconButton>
                    </Box>
                    <Divider />
                    {isOpen && (
                      <Box className={classes.details}>
                        {BusinessHours ? (
                          BusinessHours.dia_semana?.map(day => (
                            <Box
                              key={day}
                              style={{ backgroundColor: '#fff' }}
                              display='flex'
                              justifyContent='space-between'
                              alignItems='center'
                              my={1}
                            >
                              <Typography variant='body2'>
                                {day}
                                -feira
                              </Typography>
                              <Box
                                display='flex'
                                alignItems='center'
                                flexDirection={['column', 'row']}
                              >
                                <TimerIcon className={classes.iconSize} />
                                {BusinessHours.hora_inicio_manha !== 'None' &&
                                  BusinessHours.hora_fim_tarde !== 'None' ? (
                                  <>
                                    {BusinessHours.hora_fim_manha !== 'None' &&
                                      BusinessHours.hora_inicio_tarde !==
                                      'None' ? (
                                      <>
                                        <Typography
                                          variant='body2'
                                          className={classes.timeText}
                                        >
                                          {formatTime(
                                            BusinessHours.hora_inicio_manha,
                                          )}{' '}
                                          -
                                          {formatTime(
                                            BusinessHours.hora_fim_manha,
                                          )}
                                        </Typography>
                                        <PauseCircleFilledIcon
                                          className={classes.iconSize}
                                        />
                                        <Typography
                                          variant='body2'
                                          className={classes.timeText}
                                        >
                                          {formatTime(
                                            BusinessHours.hora_inicio_tarde,
                                          )}{' '}
                                          -
                                          {formatTime(
                                            BusinessHours.hora_fim_tarde,
                                          )}
                                        </Typography>
                                      </>
                                    ) : (
                                      <Typography
                                        variant='body2'
                                        className={classes.timeText}
                                      >
                                        {formatTime(
                                          BusinessHours.hora_inicio_manha,
                                        )}{' '}
                                        Às{' '}
                                        {formatTime(
                                          BusinessHours.hora_fim_tarde,
                                        )}
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  <Typography
                                    variant='body2'
                                    className={classes.timeText}
                                  >
                                    Horário não disponível
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          ))
                        ) : (
                          <Typography
                            variant='body2'
                            style={{ padding: '10px 0px' }}
                          >
                            Nenhum horário disponível no momento!
                          </Typography>
                        )}
                        <Divider />
                        <Typography
                          variant='caption'
                          color='textSecondary'
                          style={{ paddingTop: '8px' }}
                        >
                          Horários podem variar
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
